import React, { useState } from 'react';
import WorkplaceCalendar from '../../../../Components/Calendar/WorkplaceCalendar';
import HealthPlanWrapperCollapse from '../../../../Components/HealthPlan/HealthPlanWrapper/HealthPlanWrapperCollapse';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import { useAppContext } from '../../../../Services/AppContext';
import { useHealthPlanContext } from '../../../../Services/HealthPlanContext';
import useWorkplaceRole from '../../../Workplace/Hooks/useWorkplaceRole';
import HealthPlanUserInformation from '../HealthPlanUserInformation';
import HealthPlanBabyCreate from './HealthPlanBabyCreate';
import HealthPlanModal from '../HealthPlanModal';
import BabyHealthData from '../../../HealthData/Components/BabyHealthData';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import { CALENDAR_EVENT_TYPE } from '../../../../Components/Calendar/CalendarEvent/CalendarEvent';
import { useLazyMetaQuery } from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import HealthPlanQuestionnaireRevision from './HealthPlanQuestionnaireRevision';
import SubTabNavigationStepperItems from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepperItems';
import HealthPlanLoader from '../HealthPlanLoader/HealthPlanLoader';

interface HealthPlanBabyLatamProps {
	patientId: string;
	workplaceId: string;
}

const handleHealthPlanOverviewSuccess = (
	params: NavigationStepperComponentParams,
): void => params.setActiveStepPath('monitoring');

const HealthPlanBabyLatam: React.FC<HealthPlanBabyLatamProps> = ({
	patientId,
	workplaceId,
}) => {
	const { healthPlan, healthPlanUser } = useHealthPlanContext();
	const {
		userData: { id: userId },
	} = useAppContext();
	const { isDoctor, isNurse } = useWorkplaceRole({ userId, workplaceId });
	const [getMeta] = useLazyMetaQuery();
	const [userFinishedFirstStep, setUserFinishedFirstStep] = useState(false);

	useEffectAsync(async () => {
		const { meta: userMeta } = await getMeta({
			userId: patientId,
			key: 'healthPlanUserInformationFilled',
			useOriginalId: true,
		}).unwrap();

		if (userMeta) {
			setUserFinishedFirstStep(true);
		}
	}, []);

	let initialStepKey = 'healthPlanBabyCreate';

	let navigationSteps: NavigationStepperItemType[] = [
		{
			name: 'component.baby.healthPlanBabyCreate',
			key: 'healthPlanBabyCreate',
			component: () => (
				<HealthPlanBabyCreate patientId={patientId} workplaceId={workplaceId} />
			),
		},
	];

	if (healthPlanUser?.ready) {
		initialStepKey = 'healthPlan';
		navigationSteps = [
			{
				name: 'component.baby.healthPlan',
				key: 'healthPlan',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanLoader
						patientId={patientId}
						onSuccess={() => handleHealthPlanOverviewSuccess(params)}
						showContinue={false}
						modalTitle='component.baby.infoModal.title'
						modalExtraText={['component.baby.infoModal.extra']}
						modalItems={12}
						modalItemPrefix='component.baby.infoModal.items'
						modalComponent={HealthPlanModal}
						wrapperCollapseTitle=''
						wrapperCollapse={HealthPlanWrapperCollapse}
						setIsLoading={params.setIsLoading}
						isLoading={params.isLoading}
						healthPlanCode={healthPlan.code as string}
					/>
				),
			},
			{
				name: 'component.baby.monitoring',
				key: 'monitoring',
				component: () => <BabyHealthData patientId={patientId} />,
			},
			{
				name: 'base.createdHealthPlan',
				key: 'createdHealthPlan',
				component: (params: NavigationStepperComponentParams) =>
					healthPlan.doctorInitialQuestionnaireCode ? (
						<HealthPlanQuestionnaireRevision
							patientId={patientId}
							readOnly
							setIsLoading={params.setIsLoading}
						/>
					) : (
						<></>
					),
			},
		];
	}

	if (isNurse && !isDoctor) {
		initialStepKey = userFinishedFirstStep ? 'calendar' : 'patientInformation';
		const nurseSteps = [];
		if (!userFinishedFirstStep) {
			nurseSteps.push({
				name: 'component.baby.informationAboutPatient',
				key: 'patientInformation',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanUserInformation
						patientId={patientId}
						onSuccess={() => handleAfterUserInformation(params)}
						setIsLoading={params.setIsLoading}
					/>
				),
			});
		}

		nurseSteps.push({
			name: 'component.baby.calendar',
			key: 'calendar',
			component: (params: NavigationStepperComponentParams) => (
				<WorkplaceCalendar
					isNurseView
					workplaceId={workplaceId}
					patientId={patientId}
					healthPlanCode={HealthPlanCode.BABY}
					calendarEventType={CALENDAR_EVENT_TYPE.HEALTH_PLAN_EVENT}
					setIsLoading={params.setIsLoading}
				/>
			),
		});

		navigationSteps = nurseSteps;
	}

	const handleAfterUserInformation = (
		params: NavigationStepperComponentParams,
	) => params.setActiveStepPath('calendar');

	return (
		<NavigationStepper
			steps={navigationSteps}
			initialStepPath={initialStepKey}
			canSkip
			renderItems={({ items, activeKey, onChange }) => (
				<SubTabNavigationStepperItems
					items={items}
					activeKey={activeKey}
					onChange={onChange}
					firstUpper={true}
				/>
			)}
		/>
	);
};

export default HealthPlanBabyLatam;
